import { config } from "@/config/envConfig";
import useApiStore from "@/stores/api";
import useAuthStore from "@/stores/auth";
import { useDebounceFn, useWebSocket } from "@vueuse/core";
import axios from "axios";
import { ref, watch } from "vue";
import { useLocationSelector } from "./location";
import {
  SOCKET_EVENT,
  type STATUS,
  type STATUS_ID,
  type STATUS_LABEL,
} from "@/stores/constants";
import { useAuth } from "./auth";

const { DISABLE_WEBSOCKETS } = config;

const LIVE_UPDATE_QUEUE_TIMEOUT = 2000;
const LIVE_UPDATE_QUEUE_MAX_TIMEOUT = 5000;
const MESSAGE_REQUEST = "message:request";

export type MovementEventData = {
  id: number;
  pk: string;
  location?: {
    pk: string;
  };
  anonymizedAt: string | null;
  company: { pk: string };
  deletedAt: string | null;
  device: null;
  finishedAt: string | null;
  object: "movement";
  review: {
    status: STATUS | null;
    reviewer: string | null;
    reviewedAt: string | null;
  };
  startedAt: string;
  status: { id: STATUS_ID; label: STATUS_LABEL };
  timezone: string;
  user: { pk: string };
  affectedAttributes?: { reviewStatus?: STATUS };
};

export type VisitorEventData = {
  id: number;
  pk: string;
  location?: {
    pk: string;
  };
  anonymizedAt: string | null;
  company: { pk: string };
  companyName: string;
  createdAt: string;
  deletedAt: string | null;
  fullName: string;
  host: { pk: string };
  object: "visit";
  review: {
    status: STATUS | null;
    reviewer: string | null;
    reviewedAt: string | null;
  };
  timezone: string;
  updatedAt: string;
  visitAt: string;
  visitFactAt: string;
  visitFactTo: string | null;
  visitTill: string;
  visitor: { pk: string };
};

export type PreregistrationEventData = {
  id: number;
  pk: string;
  address: { description: string; location: string };
  category: { pk: string } | null;
  companyName: string | null;
  createdAt: string;
  email: string | null;
  endDate: string;
  fullName: string;
  host: { pk: string } | null;
  induction: boolean;
  location: { pk: string };
  notify: boolean;
  object: "preregistration";
  startDate: string;
  startTime: string;
  timezone: string;
  updatedAt: string;
};

export type DeliveryEventData = {
  collectedAt: null;
  company: { pk: string } | null;
  createdAt: string;
  deletedAt: string | null;
  deliveredAt: string;
  id: number;
  location: { pk: string };
  object: "delivery";
  pk: string;
  recipient: { pk: string };
  updatedAt: string;
};

export type BookingEventData = {
  createdAt: string;
  deletedAt: string | null;
  endTime: string;
  id: number;
  object: "spaceBooking";
  pk: string;
  spaceResource: { pk: string } | null;
  location: { pk: string } | null;
  startTime: string;
  updatedAt: string;
  user: { pk: string } | null;
};

export type AzureLocationsEventData = {
  name: string;
  total: number;
}[];

export type AzureSyncEventData = {
  issues: number;
  noActions?: {
    set: boolean;
    heldActions: {
      email: string;
      firstName: string;
      lastName: string;
    }[];
  };
  noActionsCount?: number;
  totals: {
    unchanged: number;
    created: number;
    updated: number;
    deleted: number;
  };
};

type EventDataMap = {
  [SOCKET_EVENT.MOVEMENT_NEW]: MovementEventData;
  [SOCKET_EVENT.MOVEMENT_EDIT]: MovementEventData;
  [SOCKET_EVENT.VISIT_NEW]: VisitorEventData;
  [SOCKET_EVENT.VISIT_EDIT]: VisitorEventData;
  [SOCKET_EVENT.VISIT_CLOSE]: VisitorEventData;
  [SOCKET_EVENT.VISIT_REMOVE]: VisitorEventData;
  [SOCKET_EVENT.PREREGISTRATION_NEW]: PreregistrationEventData;
  [SOCKET_EVENT.PREREGISTRATION_EDIT]: PreregistrationEventData;
  [SOCKET_EVENT.PREREGISTRATION_REMOVE]: PreregistrationEventData;
  [SOCKET_EVENT.DELIVERY_NEW]: DeliveryEventData;
  [SOCKET_EVENT.DELIVERY_EDIT]: DeliveryEventData;
  [SOCKET_EVENT.DELIVERY_REMOVE]: DeliveryEventData;
  [SOCKET_EVENT.BOOKING_NEW]: BookingEventData;
  [SOCKET_EVENT.BOOKING_EDIT]: BookingEventData;
  [SOCKET_EVENT.BOOKING_REMOVE]: BookingEventData;
  [SOCKET_EVENT.SETUP_SPACES]: Record<string, never>;
  [SOCKET_EVENT.SETUP_AZURE]: AzureLocationsEventData | AzureSyncEventData;
};

type LiveUpdateEventMap = {
  [key in keyof EventDataMap]?: {
    fn: (
      data: EventDataMap[key],
      messageId: string,
      messageEvent: string
    ) => void;
    batch?: boolean;
  };
};

interface BaseWebSocketEvent {
  event: string;
  error?: WebSocketEvent;
  code: number;
  success: string;
  messageId: string;
}

interface MovementWebSocketEvent extends BaseWebSocketEvent {
  type: SOCKET_EVENT.MOVEMENT_NEW | SOCKET_EVENT.MOVEMENT_EDIT;
  data: MovementEventData;
}

interface VisitorWebSocketEvent extends BaseWebSocketEvent {
  type:
    | SOCKET_EVENT.VISIT_NEW
    | SOCKET_EVENT.VISIT_EDIT
    | SOCKET_EVENT.VISIT_CLOSE
    | SOCKET_EVENT.VISIT_REMOVE;
  data: VisitorEventData;
}

interface PreregisrationWebSocketEvent extends BaseWebSocketEvent {
  type:
    | SOCKET_EVENT.PREREGISTRATION_NEW
    | SOCKET_EVENT.PREREGISTRATION_EDIT
    | SOCKET_EVENT.PREREGISTRATION_REMOVE;
  data: PreregistrationEventData;
}

interface DeliveryWebSocketEvent extends BaseWebSocketEvent {
  type:
    | SOCKET_EVENT.DELIVERY_NEW
    | SOCKET_EVENT.DELIVERY_EDIT
    | SOCKET_EVENT.DELIVERY_REMOVE;
  data: DeliveryEventData;
}

interface BookingWebSocketEvent extends BaseWebSocketEvent {
  type:
    | SOCKET_EVENT.BOOKING_NEW
    | SOCKET_EVENT.BOOKING_EDIT
    | SOCKET_EVENT.BOOKING_REMOVE;
  data: BookingEventData;
}

interface AzureLocationsWebSocketEvent extends BaseWebSocketEvent {
  type: SOCKET_EVENT.SETUP_AZURE;
  data: AzureLocationsEventData;
}

interface AzureSyncWebSocketEvent extends BaseWebSocketEvent {
  type: SOCKET_EVENT.SETUP_AZURE;
  data: AzureSyncEventData;
}

interface SpacesSetupWebSocketEvent extends BaseWebSocketEvent {
  type: SOCKET_EVENT.SETUP_SPACES;
  data: Record<string, never>;
}

interface MessageRequestWebSocketEvent extends BaseWebSocketEvent {
  type: "message:request";
  data: { url: string };
}

type WebSocketEvent =
  | MessageRequestWebSocketEvent
  | MovementWebSocketEvent
  | VisitorWebSocketEvent
  | PreregisrationWebSocketEvent
  | DeliveryWebSocketEvent
  | BookingWebSocketEvent
  | AzureLocationsWebSocketEvent
  | AzureSyncWebSocketEvent
  | SpacesSetupWebSocketEvent;

type EventQueueKey = `${string}-${keyof EventDataMap}`;
type EventQueueItem = {
  pk: string;
  messageId: string;
  messageEvent: string;
  eventType: SOCKET_EVENT;
  eventData: WebSocketEvent["data"];
};

const authStore = useAuthStore();
const { isSuperAdmin } = useAuth();
const apiStore = useApiStore();

const webSocketUrl = ref<string>();
const webSocket = useWebSocket(webSocketUrl, {
  immediate: false,
  autoReconnect: true,
});

watch(
  () => authStore.userEmail,
  async (userEmail) => {
    if (isSuperAdmin.value || DISABLE_WEBSOCKETS) return;

    if (userEmail) {
      const token = await authStore.getAccessToken();
      webSocketUrl.value = `${apiStore.getEndpoint(
        "push"
      )}?Authorization=${token}`;
      webSocket.open();
      return;
    }

    webSocket.close();
    webSocketUrl.value = undefined;
  },
  { immediate: true }
);

export function useLiveUpdates(eventMap: LiveUpdateEventMap) {
  const eventQueue = ref(new Map<EventQueueKey, EventQueueItem>());
  const locationSelector = useLocationSelector();

  const isEventForSelectedLocation = (locationPk?: string) => {
    if (locationSelector.isAllLocationsSelected.value) return true;
    if (!locationPk) return false;

    if (locationSelector.selectedLocationType.value === "GROUP") {
      return locationSelector.selectedLocationGroup.value?.locations.some(
        (location) => location.id === locationPk
      );
    }

    return locationSelector.selectedLocation.value.id === locationPk;
  };

  watch(webSocket.data, (data) => handleMessage(data));

  const sendEvent = (
    type: SOCKET_EVENT,
    data: WebSocketEvent["data"],
    messageId: string,
    event: string
  ) => {
    if (!eventMap[type]) return;

    switch (type) {
      case SOCKET_EVENT.MOVEMENT_NEW:
      case SOCKET_EVENT.MOVEMENT_EDIT:
        return eventMap[type]?.fn(data as MovementEventData, messageId, event);
      case SOCKET_EVENT.VISIT_NEW:
      case SOCKET_EVENT.VISIT_EDIT:
      case SOCKET_EVENT.VISIT_CLOSE:
      case SOCKET_EVENT.VISIT_REMOVE:
        return eventMap[type]?.fn(data as VisitorEventData, messageId, event);
      case SOCKET_EVENT.PREREGISTRATION_NEW:
      case SOCKET_EVENT.PREREGISTRATION_EDIT:
      case SOCKET_EVENT.PREREGISTRATION_REMOVE:
        return eventMap[type]?.fn(
          data as PreregistrationEventData,
          messageId,
          event
        );
      case SOCKET_EVENT.DELIVERY_NEW:
      case SOCKET_EVENT.DELIVERY_EDIT:
      case SOCKET_EVENT.DELIVERY_REMOVE:
        return eventMap[type]?.fn(data as DeliveryEventData, messageId, event);
      case SOCKET_EVENT.BOOKING_NEW:
      case SOCKET_EVENT.BOOKING_EDIT:
      case SOCKET_EVENT.BOOKING_REMOVE:
        return eventMap[type]?.fn(data as BookingEventData, messageId, event);
      case SOCKET_EVENT.SETUP_SPACES:
        return eventMap[type]?.fn(
          data as Record<string, never>,
          messageId,
          event
        );
      case SOCKET_EVENT.SETUP_AZURE:
        return event === "azureSync"
          ? eventMap[type]?.fn(data as AzureSyncEventData, messageId, event)
          : eventMap[type]?.fn(
              data as AzureLocationsEventData,
              messageId,
              event
            );
    }
  };

  const debounceProcessEventQueue = useDebounceFn(
    () => {
      eventQueue.value.forEach(
        ({ pk, eventType, eventData, messageId, messageEvent }) => {
          sendEvent(eventType, eventData, messageId, messageEvent);

          const key: EventQueueKey = `${pk}-${eventType}`;
          eventQueue.value.delete(key);
        }
      );
    },
    LIVE_UPDATE_QUEUE_TIMEOUT,
    { maxWait: LIVE_UPDATE_QUEUE_MAX_TIMEOUT }
  );

  const handleMessage = async (stringifiedEvent: string) => {
    let socketEvent: WebSocketEvent = JSON.parse(stringifiedEvent);

    if (socketEvent.type === MESSAGE_REQUEST && socketEvent.data.url) {
      const { data } = await axios.get(
        apiStore.getEndpoint("rest", socketEvent.data.url)
      );
      socketEvent = data;
    }

    const { type, data, messageId, event } = socketEvent;
    if (type === MESSAGE_REQUEST) return;

    if (eventMap[type]?.batch === false) {
      return sendEvent(type, data, messageId, event);
    }

    const pk = data && "pk" in data ? data.pk : messageId;
    const locationPk =
      data && "location" in data ? data?.location?.pk : undefined;

    const locationMatched = isEventForSelectedLocation(locationPk);
    if (!locationMatched) return;

    const key: EventQueueKey = `${pk}-${type}`;

    const queueContainsType = [...eventQueue.value].some(
      ([_key, value]) => value.eventType === type
    );

    if (queueContainsType || eventQueue.value.get(key)) return;

    eventQueue.value.set(key, {
      pk,
      eventType: type,
      eventData: data,
      messageId,
      messageEvent: event,
    } as EventQueueItem);
    debounceProcessEventQueue();
  };
}
